import { useState, useMemo, useCallback } from 'react';
import { Search, Plus, Users, Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMaterials, useCreateMaterial, useUpdateMaterial, useDeleteMaterial, type MaterialDTO } from '../../hooks/useMaterials';
import { useAuthStore } from '../../stores/auth-store';
import { createMaterialSchema, type CreateMaterialInput, type UpdateMaterialInput } from '@shared/validation/material-schemas';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { SkeletonTable } from '../../components/ui/Skeleton';

const UNIDAD_OPTIONS = [
  { value: 'm2', label: 'm²' },
  { value: 'ml', label: 'ml' },
  { value: 'unid', label: 'Unidad' },
  { value: 'kg', label: 'kg' },
  { value: 'glb', label: 'Global' },
  { value: 'lt', label: 'Litro' },
  { value: 'gal', label: 'Galón' },
  { value: 'rollo', label: 'Rollo' },
  { value: 'funda', label: 'Funda' },
  { value: 'plancha', label: 'Plancha' },
];

export function ManoDeObraTab() {
  const user = useAuthStore((s) => s.user);
  const canEdit = ['administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout>>();
  const [page, setPage] = useState(1);
  const [showInactive, setShowInactive] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMdo, setEditingMdo] = useState<MaterialDTO | null>(null);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (debounceTimer) clearTimeout(debounceTimer);
      const timer = setTimeout(() => {
        setDebouncedSearch(value);
        setPage(1);
      }, 300);
      setDebounceTimer(timer);
    },
    [debounceTimer],
  );

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      categoria: 'mdo' as const,
      isActive: showInactive ? undefined : true,
      page,
      limit: 20,
    }),
    [debouncedSearch, page, showInactive],
  );

  const { data, isLoading, isError, refetch } = useMaterials(filters);

  if (isLoading) {
    return <Card><div className="p-6"><SkeletonTable rows={5} cols={4} /></div></Card>;
  }

  if (isError) {
    return (
      <Card>
        <div className="px-6 py-12 text-center">
          <p className="text-sm text-gray-500">Error al cargar ítems de mano de obra</p>
          <Button variant="secondary" className="mt-4" onClick={() => refetch()}>Reintentar</Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white/80 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? <Eye size={14} /> : <EyeOff size={14} />}
            {showInactive ? 'Ver activos' : 'Ver inactivos'}
          </button>
        </div>
        {canEdit && (
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setShowCreateModal(true)}>
            Nueva MDO
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          className="w-full rounded-lg border border-gray-200 bg-white/80 py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
          placeholder="Buscar por nombre o SKU..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      {/* Content */}
      {data && data.data.length === 0 && !debouncedSearch ? (
        <Card>
          <div className="px-6 py-16 text-center">
            <Users size={48} className="mx-auto text-gray-300" />
            <h2 className="mt-4 text-lg font-semibold text-gray-600">No hay ítems de mano de obra aún</h2>
            <p className="mt-1 text-sm text-gray-400">Agrega el primer ítem MDO al catálogo.</p>
            {canEdit && (
              <Button variant="primary" className="mt-4" leftIcon={<Plus size={16} />} onClick={() => setShowCreateModal(true)}>
                Nueva MDO
              </Button>
            )}
          </div>
        </Card>
      ) : data && data.data.length === 0 && debouncedSearch ? (
        <Card>
          <div className="px-6 py-16 text-center">
            <Search size={48} className="mx-auto text-gray-300" />
            <h2 className="mt-4 text-lg font-semibold text-gray-600">Sin resultados</h2>
            <p className="mt-1 text-sm text-gray-400">
              No encontramos MDO que coincida con &quot;{debouncedSearch}&quot;
            </p>
          </div>
        </Card>
      ) : (
        <>
          <Card className="hidden sm:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Nombre</th>
                    <th className="px-4 py-3">SKU</th>
                    <th className="px-4 py-3">Unidad</th>
                    <th className="px-4 py-3">Tarifa</th>
                    {showInactive && <th className="px-4 py-3">Estado</th>}
                  </tr>
                </thead>
                <tbody>
                  {data?.data.map((mdo) => (
                    <MdoRow key={mdo.id} mdo={mdo} showInactive={showInactive} onClick={canEdit ? () => setEditingMdo(mdo) : undefined} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="space-y-3 sm:hidden">
            {data?.data.map((mdo) => (
              <MdoCard key={mdo.id} mdo={mdo} onClick={canEdit ? () => setEditingMdo(mdo) : undefined} />
            ))}
          </div>

          {data && data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
              <span className="text-sm text-gray-500">Página {page} de {data.pagination.totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= data.pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
            </div>
          )}
        </>
      )}

      {/* Mobile FAB */}
      {canEdit && (
        <button
          className="fixed bottom-6 right-6 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500 text-white shadow-lg transition-transform hover:scale-105 sm:hidden"
          onClick={() => setShowCreateModal(true)}
          aria-label="Nueva MDO"
        >
          <Plus size={24} />
        </button>
      )}

      <CreateMdoModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />
      {editingMdo && (
        <EditMdoModal mdo={editingMdo} onClose={() => setEditingMdo(null)} />
      )}
    </div>
  );
}

function MdoRow({ mdo, showInactive, onClick }: { mdo: MaterialDTO; showInactive: boolean; onClick?: () => void }) {
  return (
    <tr
      className={`border-b border-gray-50 transition-colors hover:bg-gray-50/50 ${!mdo.is_active ? 'opacity-50' : ''} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <td className="px-4 py-3">
        <div>
          <p className="font-medium text-gray-800">{mdo.nombre}</p>
          {mdo.presentacion && <p className="text-xs text-gray-400">{mdo.presentacion}</p>}
        </div>
      </td>
      <td className="px-4 py-3 text-gray-600">{mdo.sku ?? '—'}</td>
      <td className="px-4 py-3 text-gray-600">{mdo.unidad}</td>
      <td className="px-4 py-3 font-medium text-gray-800">${mdo.precio_unitario.toFixed(2)}</td>
      {showInactive && (
        <td className="px-4 py-3">
          <Badge color={mdo.is_active ? 'green' : 'gray'}>{mdo.is_active ? 'Activo' : 'Inactivo'}</Badge>
        </td>
      )}
    </tr>
  );
}

function MdoCard({ mdo, onClick }: { mdo: MaterialDTO; onClick?: () => void }) {
  return (
    <div className={`glass-card p-4 ${!mdo.is_active ? 'opacity-50' : ''} ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-medium text-gray-800">{mdo.nombre}</p>
          <p className="text-xs text-gray-400">{mdo.sku ?? 'Sin SKU'} — {mdo.unidad}</p>
        </div>
        <p className="font-semibold text-gray-800">${mdo.precio_unitario.toFixed(2)}</p>
      </div>
      <div className="mt-2">
        <Badge color="orange">MDO</Badge>
      </div>
    </div>
  );
}

function CreateMdoModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const createMutation = useCreateMaterial();
  const { register, handleSubmit, formState: { errors } } = useForm<CreateMaterialInput>({
    resolver: zodResolver(createMaterialSchema),
    defaultValues: { precioUnitario: 0, categoria: 'mdo', unidad: 'unid' },
  });

  const onSubmit = handleSubmit(async (data) => {
    await createMutation.mutateAsync({
      ...data,
      categoria: 'mdo',
      sku: data.sku?.trim() || null,
      presentacion: data.presentacion?.trim() || null,
    });
    onClose();
  });

  return (
    <Modal isOpen={isOpen} title="Nueva Mano de Obra" onClose={onClose} size="md">
      <form onSubmit={onSubmit} className="space-y-4">
        <Input label="Nombre" {...register('nombre')} error={errors.nombre?.message} placeholder="Ej: Maestro Gypsum" />
        <div className="grid grid-cols-2 gap-4">
          <Input label="SKU (opcional)" {...register('sku')} error={errors.sku?.message} placeholder="Ej: MDO-001" />
          <Input label="Presentación (opcional)" {...register('presentacion')} error={errors.presentacion?.message} placeholder="Ej: Por hora" />
        </div>
        <Select label="Unidad" {...register('unidad')} error={errors.unidad?.message} options={UNIDAD_OPTIONS} />
        <Input label="Tarifa" type="number" step="0.01" {...register('precioUnitario', { valueAsNumber: true })} error={errors.precioUnitario?.message} placeholder="0.00" />
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">Cancelar</Button>
          <Button variant="primary" type="submit" isLoading={createMutation.isPending}>Crear MDO</Button>
        </div>
      </form>
    </Modal>
  );
}

function EditMdoModal({ mdo, onClose }: { mdo: MaterialDTO; onClose: () => void }) {
  const updateMutation = useUpdateMaterial();
  const deleteMutation = useDeleteMaterial();
  const [nombre, setNombre] = useState(mdo.nombre);
  const [sku, setSku] = useState(mdo.sku ?? '');
  const [presentacion, setPresentacion] = useState(mdo.presentacion ?? '');
  const [unidad, setUnidad] = useState(mdo.unidad);
  const [precioUnitario, setPrecioUnitario] = useState(String(mdo.precio_unitario));

  const handleSubmit = async () => {
    if (!nombre.trim()) return;
    const data: UpdateMaterialInput = {
      nombre: nombre.trim(),
      sku: sku.trim() || null,
      presentacion: presentacion.trim() || null,
      unidad: unidad as 'm2' | 'ml' | 'unid' | 'kg' | 'glb' | 'lt' | 'gal' | 'rollo' | 'funda' | 'plancha',
      categoria: 'mdo',
      precioUnitario: Number(precioUnitario),
    };
    await updateMutation.mutateAsync({ id: mdo.id, data });
    onClose();
  };

  return (
    <Modal isOpen title="Editar Mano de Obra" onClose={onClose} size="md">
      <div className="space-y-4">
        <Input label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} />
        <div className="grid grid-cols-2 gap-4">
          <Input label="SKU (opcional)" value={sku} onChange={(e) => setSku(e.target.value)} />
          <Input label="Presentación (opcional)" value={presentacion} onChange={(e) => setPresentacion(e.target.value)} />
        </div>
        <Select label="Unidad" value={unidad} onChange={(e) => setUnidad(e.target.value)} options={UNIDAD_OPTIONS} />
        <Input label="Tarifa" type="number" step="0.01" value={precioUnitario} onChange={(e) => setPrecioUnitario(e.target.value)} />
        <div className="flex justify-between pt-2">
          <Button variant="ghost" className="text-red-500 hover:text-red-700" onClick={async () => {
            if (window.confirm('¿Desactivar este ítem MDO? No aparecerá en futuras búsquedas.')) {
              await deleteMutation.mutateAsync(mdo.id);
              onClose();
            }
          }}>Desactivar</Button>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose} type="button">Cancelar</Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              isLoading={updateMutation.isPending}
              disabled={!nombre.trim()}
            >
              Guardar Cambios
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
